import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Grid } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetVersionQuery } from "fond/api";
import { computeBomArchErrors } from "fond/architecture/bomRules";
import { useFiberPathLength, useServiceLocationsCount } from "fond/hooks/useCostToServe";
import { Architecture, BomOutput, Project, Store } from "fond/types";
import { isCollaborationProject, isPlannerProject } from "fond/utils/project";
import { Message } from "fond/widgets";

import { InteractiveBomView } from "../constants";
import { flattenBomData } from "../index";

import BomCostChart from "./BomCostChart";
import BomCostPerServiceLocationChart from "./BomCostPerServiceLocationChart";
import BomSummary from "./BomSummary";
import BomTable from "./BomTable";

interface BomContentProps {
  project: Project;
  architecture: Architecture | null;
  bom: BomOutput;
  bomArchRestrictions?: unknown;
}

const BomContent: React.FC<BomContentProps> = ({ project, architecture, bom, bomArchRestrictions }: BomContentProps) => {
  const versionId = useSelector((state: Store) => state.project.versionId);
  const { data: version } = useGetVersionQuery(versionId ?? skipToken);
  const [view, setView] = useState<InteractiveBomView>("analytics");

  const unitLabel = project.SystemOfMeasurement === "imperial" ? "ft" : "m";
  const rows = useMemo(() => flattenBomData(bom, project.SystemOfMeasurement), [bom, project.SystemOfMeasurement]);
  const partitions = "partitions" in bom ? bom.partitions : undefined;
  const totalFiberPathLength = useFiberPathLength({ project, versionId });
  const serviceLocationsCount = useServiceLocationsCount({ project, versionId });

  const isGridView = view === "grid";

  const isCollaboration = isCollaborationProject(project);
  const archErrors = useMemo(
    () => (isCollaboration ? [] : computeBomArchErrors(architecture, bomArchRestrictions, project.SystemOfMeasurement)),
    [isCollaboration, architecture, bomArchRestrictions, project.SystemOfMeasurement]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height={isGridView ? "100%" : undefined}
      minHeight={isGridView ? 0 : "100%"}
      overflow={isGridView ? "hidden" : undefined}
      px="10%"
    >
      {archErrors.length > 0 && (
        <Message type="warning" data-testid="bom-arch-validation-warning" mb={3}>
          <Box display="flex" flexDirection="column">
            <div>
              This Bill of Materials has not been configured with your architecture in mind. Your architecture contains parameters that result in
              equipment being placed that will not be counted. The equipment that won&apos;t be counted are:
            </div>
            <ul>
              {archErrors.map((e) => (
                <li key={e.key}>{e.message}</li>
              ))}
            </ul>
          </Box>
        </Message>
      )}
      <BomSummary
        totalCost={bom.total}
        totalPath={totalFiberPathLength}
        serviceLocationsCount={serviceLocationsCount}
        unitLabel={unitLabel}
        view={view}
        isPlannerProject={isPlannerProject(project)}
        currency={project.Currency}
        onViewChange={setView}
      />
      {view === "analytics" && (
        <Grid container spacing={2.5} mb={2} alignItems="stretch">
          <BomCostChart rows={rows} totalCost={bom.total} currency={project.Currency} />
          <BomCostPerServiceLocationChart project={project} versionId={versionId} currency={project.Currency} />
        </Grid>
      )}
      <Box
        height={isGridView ? undefined : 700}
        flex={isGridView ? 1 : undefined}
        minHeight={isGridView ? 0 : undefined}
        flexShrink={0}
        display="flex"
        flexDirection="column"
      >
        <BomTable rows={rows} partitions={partitions} currency={project.Currency} />
      </Box>
    </Box>
  );
};

export default BomContent;
